import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { leaveAPI, userAPI } from '../services/api';
import {
  FiCalendar, FiRefreshCw, FiSearch, FiUser, FiCheck, FiX, FiPlus, FiUsers,
} from '../components/Icons';
import {
  Card, ModernButton, ModernInput, ModernSelect, Badge,
} from '../components/SharedComponents';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const PAGE_SIZE = 50;

/* ----------------------------- styles ----------------------------- */

const Container = styled.div`
  width: 100%;
  padding: ${(p) => p.theme.spacing.lg};
  box-sizing: border-box;
  max-width: none;
`;

const HeaderCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.lg};
  flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.spacing.xl};

  h1 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${(p) => p.theme.spacing.sm};
  }

  p {
    margin: ${(p) => p.theme.spacing.xs} 0 0 0;
    color: ${(p) => p.theme.colors.text.secondary};
    font-size: 0.95rem;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border.light};
  margin-bottom: ${(p) => p.theme.spacing.lg};
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  font-family: ${(p) => p.theme.fonts.main};
  font-size: 0.92rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text.secondary};
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: color 0.15s ease, border-color 0.15s ease;

  &.active {
    border-bottom-color: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.primary};
  }

  &:hover {
    color: ${(p) => p.theme.colors.text.primary};
  }
`;

const PendingCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #b26a12;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
`;

const ControlsRow = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.md};
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.spacing.lg};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.text.secondary};
  }
`;

const SearchBox = styled.div`
  position: relative;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${(p) => p.theme.colors.text.light};
    pointer-events: none;
  }

  input {
    padding-left: 36px;
    min-width: 220px;
  }
`;

const Chips = styled.div`
  display: inline-flex;
  border: 1px solid ${(p) => p.theme.colors.border.medium};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  overflow: hidden;

  button {
    border: none;
    background: ${(p) => p.theme.colors.surface};
    color: ${(p) => p.theme.colors.text.secondary};
    padding: 8px 14px;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: ${(p) => p.theme.fonts.main};
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;

    & + button {
      border-left: 1px solid ${(p) => p.theme.colors.border.medium};
    }

    &.active {
      background: ${(p) => p.theme.colors.primary};
      color: ${(p) => p.theme.colors.text.inverse};
    }
  }
`;

const TableCard = styled(Card)`
  padding: 0;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${(p) => p.theme.fonts.main};

  th, td {
    padding: 10px 14px;
    text-align: left;
    border-bottom: 1px solid ${(p) => p.theme.colors.border.light};
  }

  th {
    background: ${(p) => p.theme.colors.surfaceAlt};
    color: ${(p) => p.theme.colors.text.secondary};
    font-weight: 600;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  td {
    font-size: 0.9rem;
    color: ${(p) => p.theme.colors.text.primary};
    vertical-align: middle;
  }

  tbody tr {
    transition: background 0.12s ease;

    &:hover { background: ${(p) => p.theme.colors.surfaceAlt}; }
    &:last-child td { border-bottom: none; }
    &.pending-row { background: #fbf4e6; }
    &.pending-row:hover { background: #f6ecd7; }
  }
`;

const BalCell = styled.td`
  min-width: 130px;

  .nums {
    font-size: 0.85rem;
    font-variant-numeric: tabular-nums;

    b { font-weight: 700; }
    span { color: ${(p) => p.theme.colors.text.secondary}; }
  }

  .track {
    margin-top: 4px;
    height: 6px;
    border-radius: 999px;
    background: ${(p) => p.theme.colors.surfaceAlt};
    border: 1px solid ${(p) => p.theme.colors.border.light};
    overflow: hidden;
  }

  .fill {
    height: 100%;
    border-radius: 999px;
  }

  .na { color: ${(p) => p.theme.colors.text.light}; font-size: 0.82rem; }
`;

const SubText = styled.div`
  font-size: 0.75rem;
  color: ${(p) => p.theme.colors.text.light};
`;

const CountNote = styled.div`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.text.secondary};
  margin-bottom: ${(p) => p.theme.spacing.sm};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${(p) => p.theme.spacing.lg};
  margin-bottom: ${(p) => p.theme.spacing.lg};
`;

const Hint = styled.div`
  margin-top: 6px;
  font-size: 0.8rem;
  padding: 8px 10px;
  border-radius: ${(p) => p.theme.borderRadius.medium};
  background: ${(p) => (p.tone === 'monthly' ? '#fbf4e6' : '#eef5f0')};
  border: 1px solid ${(p) => (p.tone === 'monthly' ? '#e3c07a' : '#cfe3d7')};
  color: ${(p) => p.theme.colors.text.secondary};
`;

const ErrorText = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.colors.status.error};
  font-size: 0.8rem;
`;

const PickerBox = styled.div`
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid ${(p) => p.theme.colors.border.medium};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  padding: ${(p) => p.theme.spacing.sm};
  background: ${(p) => p.theme.colors.surface};

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 8px;
    border-radius: ${(p) => p.theme.borderRadius.small};
    font-size: 0.9rem;
    cursor: pointer;

    &:hover { background: ${(p) => p.theme.colors.surfaceAlt}; }

    input {
      accent-color: ${(p) => p.theme.colors.primary};
    }

    .role { color: ${(p) => p.theme.colors.text.light}; font-size: 0.78rem; }
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.92rem;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: ${(p) => p.theme.colors.primary};
    cursor: pointer;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(43, 40, 37, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: ${(p) => p.theme.spacing.md};
`;

const ModalContent = styled.div`
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.large};
  box-shadow: ${(p) => p.theme.shadows.heavy};
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: ${(p) => p.theme.spacing.xl};

  h3 {
    margin: 0;
    font-size: 1.25rem;
  }
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${(p) => p.theme.spacing.lg};
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.medium};
  background: ${(p) => p.theme.colors.surface};
  color: ${(p) => p.theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.colors.surfaceAlt};
    color: ${(p) => p.theme.colors.text.primary};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${(p) => p.theme.spacing.sm};
  margin-top: ${(p) => p.theme.spacing.xl};
  padding-top: ${(p) => p.theme.spacing.lg};
  border-top: 1px solid ${(p) => p.theme.colors.border.light};
`;

/* --------------------------- component ---------------------------- */

const statusVariant = (s) => (s === 'approved' ? 'success' : s === 'pending' ? 'warning' : s === 'rejected' ? 'error' : 'default');
const pretty = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const LeaveManagementPage = () => {
  const [activeTab, setActiveTab] = useState('balances');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);

  // Balances tab filters
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [balBranch, setBalBranch] = useState('');
  const [balSearch, setBalSearch] = useState('');

  // Requests tab filters
  const [reqStatus, setReqStatus] = useState('pending');
  const [reqSearch, setReqSearch] = useState('');
  const [reqShown, setReqShown] = useState(PAGE_SIZE);

  // Bulk assignment form
  const [bulkForm, setBulkForm] = useState({
    leave_type_id: '', allocated_days: '', year: new Date().getFullYear(), month: '',
  });
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [assignToAll, setAssignToAll] = useState(true);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [bulkSubmitted, setBulkSubmitted] = useState(false);

  // Record-leave modal
  const [showRecord, setShowRecord] = useState(false);
  const [recordForm, setRecordForm] = useState({ user_id: '', leave_type_id: '', leave_date: '', reason: '', approve: true });
  const [recordSubmitted, setRecordSubmitted] = useState(false);
  const [recordSearch, setRecordSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const lastFetchTimeRef = useRef(0);

  useEffect(() => {
    fetchLeaveTypes();
    fetchEmployees();
    fetchLeaveBalances();
    fetchLeaveRequests();

    const handleFocus = () => {
      const now = Date.now();
      if (now - lastFetchTimeRef.current > 30000) {
        fetchLeaveBalances();
        fetchLeaveRequests();
        lastFetchTimeRef.current = now;
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveAPI.getLeaveTypes();
      setLeaveTypes(response.data);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await userAPI.getAll();
      const active = response.data
        .filter((u) => u.is_active)
        .sort((a, b) => a.name.localeCompare(b.name));
      setEmployees(active);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchLeaveBalances = async () => {
    try {
      const response = await leaveAPI.getLeaveBalances();
      setLeaveBalances(response.data);
    } catch (error) {
      console.error('Error fetching leave balances:', error);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      const response = await leaveAPI.getLeaveRequests();
      setLeaveRequests(response.data);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    }
  };

  /* ------------------------- bulk assignment ------------------------ */

  const selectedLeaveType = leaveTypes.find((lt) => lt.id === parseInt(bulkForm.leave_type_id));
  const isMonthlyQuota = selectedLeaveType?.quota_type === 'monthly';

  const bulkErrors = {
    type: !bulkForm.leave_type_id ? 'Please select a leave type' : null,
    days: !bulkForm.allocated_days || parseInt(bulkForm.allocated_days) <= 0 ? 'Enter a valid number of days' : null,
    month: isMonthlyQuota && !bulkForm.month ? 'Month is required for monthly leave types' : null,
    who: !assignToAll && selectedEmployees.length === 0 ? 'Select at least one employee' : null,
  };
  const bulkInvalid = Object.values(bulkErrors).some(Boolean);

  const handleBulkAssignment = async () => {
    setBulkSubmitted(true);
    if (bulkInvalid) return;

    const targetLabel = assignToAll
      ? `ALL ${employees.length} active employees`
      : `${selectedEmployees.length} selected employee${selectedEmployees.length === 1 ? '' : 's'}`;
    const periodLabel = isMonthlyQuota ? `${MONTHS[bulkForm.month - 1]} ${bulkForm.year}` : `year ${bulkForm.year}`;
    if (!window.confirm(`Assign ${bulkForm.allocated_days} day(s) of "${selectedLeaveType.name}" for ${periodLabel} to ${targetLabel}?`)) return;

    setLoading(true);
    try {
      const payload = {
        ...bulkForm,
        allocated_days: parseInt(bulkForm.allocated_days),
        leave_type_id: parseInt(bulkForm.leave_type_id),
        user_ids: assignToAll ? null : selectedEmployees,
        month: isMonthlyQuota ? parseInt(bulkForm.month) : null,
      };
      const response = await leaveAPI.bulkAssignLeave(payload);
      toast.success(response.data.message || 'Leave quota assigned');
      fetchLeaveBalances();
      setBulkForm({ leave_type_id: '', allocated_days: '', year: new Date().getFullYear(), month: '' });
      setSelectedEmployees([]);
      setBulkSubmitted(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------- requests ----------------------------- */

  const handleApproveRequest = async (request) => {
    if (!window.confirm(`Approve ${request.leave_type?.name} for ${request.user?.name} on ${request.leave_date}? This uses 1 day of their quota.`)) return;
    setActingId(request.id);
    try {
      await leaveAPI.approveLeaveRequest(request.id);
      toast.success(`Approved — ${request.user?.name}, ${request.leave_date}`);
      fetchLeaveRequests();
      fetchLeaveBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setActingId(null);
    }
  };

  const handleRejectRequest = async (request) => {
    if (!window.confirm(`Reject ${request.leave_type?.name} for ${request.user?.name} on ${request.leave_date}?`)) return;
    setActingId(request.id);
    try {
      await leaveAPI.rejectLeaveRequest(request.id);
      toast.success(`Rejected — ${request.user?.name}, ${request.leave_date}`);
      fetchLeaveRequests();
      fetchLeaveBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setActingId(null);
    }
  };

  const pendingCount = leaveRequests.filter((r) => r.status === 'pending').length;
  const reqCounts = useMemo(() => ({
    all: leaveRequests.length,
    pending: pendingCount,
    approved: leaveRequests.filter((r) => r.status === 'approved').length,
    rejected: leaveRequests.filter((r) => r.status === 'rejected').length,
  }), [leaveRequests, pendingCount]);

  const filteredRequests = useMemo(() => {
    let rows = leaveRequests;
    if (reqStatus !== 'all') rows = rows.filter((r) => r.status === reqStatus);
    if (reqSearch.trim()) {
      const q = reqSearch.toLowerCase();
      rows = rows.filter((r) => (r.user?.name || '').toLowerCase().includes(q));
    }
    // Pending first, then newest leave date first.
    return [...rows].sort((a, b) => {
      const pa = a.status === 'pending' ? 0 : 1;
      const pb = b.status === 'pending' ? 0 : 1;
      if (pa !== pb) return pa - pb;
      return a.leave_date < b.leave_date ? 1 : -1;
    });
  }, [leaveRequests, reqStatus, reqSearch]);

  /* --------------------------- balances ----------------------------- */

  const branches = useMemo(() => {
    const map = new Map();
    employees.forEach((e) => {
      if (e.home_branch) map.set(e.home_branch.id, e.home_branch.name);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [employees]);

  const balanceRows = useMemo(() => {
    let rows = employees;
    if (balBranch) rows = rows.filter((e) => String(e.home_branch_id) === balBranch);
    if (balSearch.trim()) {
      const q = balSearch.toLowerCase();
      rows = rows.filter((e) => e.name.toLowerCase().includes(q));
    }
    return rows;
  }, [employees, balBranch, balSearch]);

  /* ------------------------- record leave --------------------------- */

  const recordErrors = {
    who: !recordForm.user_id ? 'Select an employee' : null,
    type: !recordForm.leave_type_id ? 'Select a leave type' : null,
    date: !recordForm.leave_date ? 'Pick a date' : null,
  };
  const recordInvalid = Object.values(recordErrors).some(Boolean);

  const submitRecord = async () => {
    setRecordSubmitted(true);
    if (recordInvalid) return;
    setSaving(true);
    try {
      await leaveAPI.createLeaveRequest({
        user_id: parseInt(recordForm.user_id),
        leave_type_id: parseInt(recordForm.leave_type_id),
        leave_date: recordForm.leave_date,
        reason: recordForm.reason || null,
        status: recordForm.approve ? 'approved' : 'pending',
      });
      const who = employees.find((e) => e.id === parseInt(recordForm.user_id));
      toast.success(`Leave recorded for ${who ? who.name : 'employee'} on ${recordForm.leave_date}${recordForm.approve ? ' (approved)' : ' (pending)'}`);
      setShowRecord(false);
      setRecordForm({ user_id: '', leave_type_id: '', leave_date: '', reason: '', approve: true });
      setRecordSubmitted(false);
      setRecordSearch('');
      fetchLeaveRequests();
      fetchLeaveBalances();
    } catch (error) {
      // Backend validates duplicates and quota: surface its message directly.
      toast.error(error.response?.data?.detail || error.message);
    } finally {
      setSaving(false);
    }
  };

  const recordCandidates = useMemo(() => {
    if (!recordSearch.trim()) return employees;
    const q = recordSearch.toLowerCase();
    return employees.filter((e) => e.name.toLowerCase().includes(q) || (e.role || '').includes(q));
  }, [employees, recordSearch]);

  const filteredPickerEmployees = employees.filter((employee) => {
    if (!employeeSearchTerm.trim()) return true;
    const q = employeeSearchTerm.toLowerCase();
    return employee.name.toLowerCase().includes(q)
      || (employee.role || '').toLowerCase().includes(q)
      || (employee.email || '').toLowerCase().includes(q);
  });

  const periodLabel = new Date(filterYear, filterMonth - 1).toLocaleDateString('default', { month: 'long', year: 'numeric' });

  return (
    <Container>
      <HeaderCard>
        <div>
          <h1><FiCalendar size={24} /> Leave Management</h1>
          <p>Approve requests, check remaining quotas, and assign leave allowances.</p>
        </div>
        <ModernButton onClick={() => setShowRecord(true)}>
          <FiPlus size={16} /> Record leave
        </ModernButton>
      </HeaderCard>

      <TabContainer>
        <Tab className={activeTab === 'balances' ? 'active' : ''} onClick={() => setActiveTab('balances')}>
          Leave balances
        </Tab>
        <Tab className={activeTab === 'requests' ? 'active' : ''} onClick={() => setActiveTab('requests')}>
          Leave requests {pendingCount > 0 && <PendingCount>{pendingCount}</PendingCount>}
        </Tab>
        <Tab className={activeTab === 'bulk-assign' ? 'active' : ''} onClick={() => setActiveTab('bulk-assign')}>
          Bulk assignment
        </Tab>
      </TabContainer>

      {/* ---------------------------- balances --------------------------- */}
      {activeTab === 'balances' && (
        <Card>
          <ControlsRow>
            <Field>
              <label>Year</label>
              <ModernSelect value={filterYear} onChange={(e) => setFilterYear(Number(e.target.value))} style={{ width: 'auto' }}>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </ModernSelect>
            </Field>
            <Field>
              <label>Month</label>
              <ModernSelect value={filterMonth} onChange={(e) => setFilterMonth(Number(e.target.value))} style={{ width: 'auto' }}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </ModernSelect>
            </Field>
            <Field>
              <label>Branch</label>
              <ModernSelect value={balBranch} onChange={(e) => setBalBranch(e.target.value)} style={{ width: 'auto' }}>
                <option value="">All branches</option>
                {branches.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </ModernSelect>
            </Field>
            <Field>
              <label>Employee</label>
              <SearchBox>
                <FiSearch size={15} />
                <ModernInput value={balSearch} onChange={(e) => setBalSearch(e.target.value)} placeholder="Search by name…" />
              </SearchBox>
            </Field>
            <ModernButton variant="secondary" onClick={fetchLeaveBalances}>
              <FiRefreshCw size={15} /> Refresh
            </ModernButton>
          </ControlsRow>

          <CountNote>
            {balanceRows.length} employee{balanceRows.length === 1 ? '' : 's'} · {periodLabel} — yearly types show {filterYear}, monthly types show {MONTHS[filterMonth - 1]} {filterYear}. Cell = used / allocated.
          </CountNote>

          <TableCard>
            <Table>
              <thead>
                <tr>
                  <th>Employee</th>
                  {leaveTypes.map((lt) => (
                    <th key={lt.id}>
                      {lt.name}
                      <SubText>{lt.quota_type === 'yearly' ? `year ${filterYear}` : `${MONTHS[filterMonth - 1].slice(0, 3)} ${filterYear}`}</SubText>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {balanceRows.map((emp) => {
                  const rowBalances = {};
                  leaveBalances.forEach((b) => {
                    if (b.user?.id !== emp.id) return;
                    const lt = b.leave_type;
                    if (!lt) return;
                    if (lt.quota_type === 'monthly') {
                      if (b.year === filterYear && b.month === filterMonth) rowBalances[lt.id] = b;
                    } else if (b.year === filterYear) {
                      rowBalances[lt.id] = b;
                    }
                  });

                  return (
                    <tr key={emp.id}>
                      <td>
                        <strong>{emp.name}</strong>
                        <SubText>{emp.home_branch?.name || '—'} · {(emp.role || '').replace(/_/g, ' ')}</SubText>
                      </td>
                      {leaveTypes.map((lt) => {
                        const bal = rowBalances[lt.id];
                        if (!bal) {
                          return <BalCell key={`${emp.id}-${lt.id}`}><span className="na">—</span></BalCell>;
                        }
                        const used = bal.used_days || 0;
                        const alloc = bal.allocated_days || 0;
                        const left = bal.remaining_days != null ? bal.remaining_days : alloc - used;
                        const ratio = alloc > 0 ? used / alloc : 0;
                        const color = ratio >= 1 ? '#c5372c' : ratio >= 0.75 ? '#b26a12' : '#2e7d5b';
                        return (
                          <BalCell key={`${emp.id}-${lt.id}`}>
                            <div className="nums"><b>{used}</b><span> / {alloc} · {left} left</span></div>
                            <div className="track"><div className="fill" style={{ width: `${Math.min(100, ratio * 100)}%`, background: color }} /></div>
                          </BalCell>
                        );
                      })}
                    </tr>
                  );
                })}
                {balanceRows.length === 0 && (
                  <tr><td colSpan={1 + leaveTypes.length} style={{ color: '#9c948a', textAlign: 'center', padding: 24 }}>No employees match this filter.</td></tr>
                )}
              </tbody>
            </Table>
          </TableCard>
        </Card>
      )}

      {/* ---------------------------- requests --------------------------- */}
      {activeTab === 'requests' && (
        <Card>
          <ControlsRow>
            <Field>
              <label>Status</label>
              <Chips>
                {[['pending', `Pending (${reqCounts.pending})`], ['approved', `Approved (${reqCounts.approved})`], ['rejected', `Rejected (${reqCounts.rejected})`], ['all', `All (${reqCounts.all})`]].map(([key, labelText]) => (
                  <button key={key} type="button" className={reqStatus === key ? 'active' : ''} onClick={() => { setReqStatus(key); setReqShown(PAGE_SIZE); }}>
                    {labelText}
                  </button>
                ))}
              </Chips>
            </Field>
            <Field>
              <label>Employee</label>
              <SearchBox>
                <FiSearch size={15} />
                <ModernInput value={reqSearch} onChange={(e) => { setReqSearch(e.target.value); setReqShown(PAGE_SIZE); }} placeholder="Search by name…" />
              </SearchBox>
            </Field>
            <ModernButton variant="secondary" onClick={() => { fetchLeaveRequests(); fetchLeaveBalances(); }}>
              <FiRefreshCw size={15} /> Refresh
            </ModernButton>
          </ControlsRow>

          {reqStatus === 'pending' && filteredRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#2e7d5b', fontWeight: 600 }}>
              <FiCheck size={22} style={{ verticalAlign: '-5px', marginRight: 8 }} />
              No pending requests — everything has been reviewed.
            </div>
          ) : (
            <>
              <CountNote>{filteredRequests.length} request{filteredRequests.length === 1 ? '' : 's'}{filteredRequests.length > reqShown ? ` · showing first ${reqShown}` : ''}</CountNote>
              <TableCard>
                <Table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Leave type</th>
                      <th>Date</th>
                      <th>Reason</th>
                      <th>Status</th>
                      <th>Decided by</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.slice(0, reqShown).map((request) => (
                      <tr key={request.id} className={request.status === 'pending' ? 'pending-row' : ''}>
                        <td><strong>{request.user?.name}</strong></td>
                        <td>{request.leave_type?.name}</td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{request.leave_date}</td>
                        <td style={{ color: '#6b645c', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={request.reason || ''}>{request.reason || '—'}</td>
                        <td><Badge variant={statusVariant(request.status)}>{pretty(request.status)}</Badge></td>
                        <td style={{ color: '#6b645c', fontSize: '0.85rem' }}>{request.approver?.name || '—'}</td>
                        <td>
                          {request.status === 'pending' ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <ModernButton size="small" disabled={actingId === request.id} onClick={() => handleApproveRequest(request)}>
                                <FiCheck size={13} /> Approve
                              </ModernButton>
                              <ModernButton size="small" variant="secondary" disabled={actingId === request.id} onClick={() => handleRejectRequest(request)}>
                                <FiX size={13} /> Reject
                              </ModernButton>
                            </div>
                          ) : <span style={{ color: '#9c948a' }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableCard>
              {filteredRequests.length > reqShown && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <ModernButton variant="secondary" onClick={() => setReqShown(reqShown + PAGE_SIZE)}>
                    Show {Math.min(PAGE_SIZE, filteredRequests.length - reqShown)} more
                  </ModernButton>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* -------------------------- bulk assign -------------------------- */}
      {activeTab === 'bulk-assign' && (
        <Card>
          <FormGrid>
            <Field>
              <label>Leave type *</label>
              <ModernSelect
                value={bulkForm.leave_type_id}
                onChange={(e) => setBulkForm({ ...bulkForm, leave_type_id: e.target.value, month: '' })}
              >
                <option value="">Select leave type</option>
                {leaveTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.name} ({type.quota_type})</option>
                ))}
              </ModernSelect>
              {selectedLeaveType && (
                <Hint tone={selectedLeaveType.quota_type}>
                  {selectedLeaveType.quota_type === 'monthly'
                    ? 'Monthly quota — allocated per month; pick which month below.'
                    : 'Yearly quota — one allocation covers the whole year.'}
                </Hint>
              )}
              {bulkSubmitted && bulkErrors.type && <ErrorText>{bulkErrors.type}</ErrorText>}
            </Field>

            <Field>
              <label>Allocated days *</label>
              <ModernInput
                type="number"
                value={bulkForm.allocated_days}
                onChange={(e) => setBulkForm({ ...bulkForm, allocated_days: e.target.value })}
                min="0"
                placeholder="Number of days"
              />
              {bulkSubmitted && bulkErrors.days && <ErrorText>{bulkErrors.days}</ErrorText>}
            </Field>

            <Field>
              <label>Year *</label>
              <ModernInput
                type="number"
                value={bulkForm.year}
                onChange={(e) => setBulkForm({ ...bulkForm, year: parseInt(e.target.value) })}
                min="2020"
                max="2030"
              />
            </Field>

            {isMonthlyQuota && (
              <Field>
                <label>Month *</label>
                <ModernSelect
                  value={bulkForm.month}
                  onChange={(e) => setBulkForm({ ...bulkForm, month: e.target.value })}
                >
                  <option value="">Select month</option>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </ModernSelect>
                {bulkSubmitted && bulkErrors.month && <ErrorText>{bulkErrors.month}</ErrorText>}
              </Field>
            )}
          </FormGrid>

          <div style={{ marginBottom: 16 }}>
            <CheckboxLabel>
              <input type="checkbox" checked={assignToAll} onChange={(e) => setAssignToAll(e.target.checked)} />
              Assign to all active employees ({employees.length})
            </CheckboxLabel>
          </div>

          {!assignToAll && (
            <div style={{ marginBottom: 16, maxWidth: 560 }}>
              <ControlsRow style={{ marginBottom: 8 }}>
                <SearchBox style={{ flex: 1 }}>
                  <FiSearch size={15} />
                  <ModernInput
                    value={employeeSearchTerm}
                    onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                    placeholder="Search by name, role, or email…"
                  />
                </SearchBox>
                <ModernButton
                  size="small"
                  variant="secondary"
                  type="button"
                  onClick={() => setSelectedEmployees(Array.from(new Set([...selectedEmployees, ...filteredPickerEmployees.map((e) => e.id)])))}
                >
                  Select shown
                </ModernButton>
                <ModernButton size="small" variant="secondary" type="button" onClick={() => setSelectedEmployees([])}>
                  Clear
                </ModernButton>
              </ControlsRow>
              <CountNote><FiUsers size={13} style={{ verticalAlign: '-2px' }} /> {selectedEmployees.length} selected</CountNote>
              <PickerBox>
                {filteredPickerEmployees.map((employee) => (
                  <label key={employee.id}>
                    <input
                      type="checkbox"
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedEmployees([...selectedEmployees, employee.id]);
                        else setSelectedEmployees(selectedEmployees.filter((id) => id !== employee.id));
                      }}
                    />
                    {employee.name}
                    <span className="role">{(employee.role || '').replace(/_/g, ' ')} · {employee.home_branch?.name || '—'}</span>
                  </label>
                ))}
                {filteredPickerEmployees.length === 0 && <div style={{ color: '#9c948a', padding: 8 }}>No matches.</div>}
              </PickerBox>
              {bulkSubmitted && bulkErrors.who && <ErrorText>{bulkErrors.who}</ErrorText>}
            </div>
          )}

          <ModernButton onClick={handleBulkAssignment} disabled={loading}>
            {loading ? 'Assigning…' : 'Assign leave quota'}
          </ModernButton>
        </Card>
      )}

      {/* ------------------------- record modal -------------------------- */}
      {showRecord && (
        <ModalOverlay onMouseDown={(e) => { if (e.target === e.currentTarget) setShowRecord(false); }}>
          <ModalContent>
            <ModalTitleRow>
              <h3><FiUser size={18} style={{ verticalAlign: '-3px', marginRight: 6 }} /> Record leave</h3>
              <CloseButton type="button" onClick={() => setShowRecord(false)} aria-label="Close"><FiX size={16} /></CloseButton>
            </ModalTitleRow>

            <Field style={{ marginBottom: 16 }}>
              <label>Employee *</label>
              <SearchBox>
                <FiSearch size={15} />
                <ModernInput value={recordSearch} onChange={(e) => setRecordSearch(e.target.value)} placeholder="Type to filter the list below…" />
              </SearchBox>
              <ModernSelect
                value={recordForm.user_id}
                onChange={(e) => setRecordForm({ ...recordForm, user_id: e.target.value })}
                size={Math.min(6, Math.max(3, recordCandidates.length))}
                style={{ marginTop: 6 }}
              >
                {recordCandidates.map((e) => (
                  <option key={e.id} value={e.id}>{e.name} — {(e.role || '').replace(/_/g, ' ')} ({e.home_branch?.name || '—'})</option>
                ))}
              </ModernSelect>
              {recordSubmitted && recordErrors.who && <ErrorText>{recordErrors.who}</ErrorText>}
            </Field>

            <Field style={{ marginBottom: 16 }}>
              <label>Leave type *</label>
              <ModernSelect value={recordForm.leave_type_id} onChange={(e) => setRecordForm({ ...recordForm, leave_type_id: e.target.value })}>
                <option value="">Select leave type</option>
                {leaveTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.name} ({type.quota_type})</option>
                ))}
              </ModernSelect>
              {recordSubmitted && recordErrors.type && <ErrorText>{recordErrors.type}</ErrorText>}
            </Field>

            <Field style={{ marginBottom: 16 }}>
              <label>Date *</label>
              <ModernInput type="date" value={recordForm.leave_date} onChange={(e) => setRecordForm({ ...recordForm, leave_date: e.target.value })} />
              {recordSubmitted && recordErrors.date && <ErrorText>{recordErrors.date}</ErrorText>}
            </Field>

            <Field style={{ marginBottom: 16 }}>
              <label>Reason (optional)</label>
              <ModernInput value={recordForm.reason} onChange={(e) => setRecordForm({ ...recordForm, reason: e.target.value })} placeholder="e.g. personal errand" />
            </Field>

            <CheckboxLabel>
              <input type="checkbox" checked={recordForm.approve} onChange={(e) => setRecordForm({ ...recordForm, approve: e.target.checked })} />
              Approve immediately (uses 1 day of quota now)
            </CheckboxLabel>

            <ModalFooter>
              <ModernButton variant="secondary" type="button" onClick={() => setShowRecord(false)} disabled={saving}>Cancel</ModernButton>
              <ModernButton type="button" onClick={submitRecord} disabled={saving}>
                {saving ? 'Saving…' : 'Record leave'}
              </ModernButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default LeaveManagementPage;
